import { useEffect, useRef, useState } from 'react';

const WhyChooseUs = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const benefits = [
    {
      icon: 'ri-star-line',
      title: 'Premium Quality Equipment',
      description: 'Only the finest glassware and bar equipment for your event'
    },
    {
      icon: 'ri-sparkling-2-line',
      title: 'Cleaned & Professionally Packed',
      description: 'Every item arrives spotless and ready for service'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Reliable Scheduling',
      description: 'On-time delivery and collection, every time'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Transparent Pricing',
      description: 'Clear rates with no hidden fees or surprises'
    },
    {
      icon: 'ri-team-line',
      title: 'Experienced Event Support',
      description: 'Professional guidance from planning to pack-down'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-serif text-gray-900 mb-4">
            Why House of FMR?
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-amber-600 to-transparent mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We understand that presentation matters. Our team ensures every item arrives spotless, 
            organised, and ready for service.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`group transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="bg-white rounded-lg p-8 h-full border border-gray-100 hover:border-amber-600 hover:shadow-xl transition-all duration-300">
                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-50 to-amber-100 rounded-full mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${benefit.icon} text-3xl text-amber-600`}></i>
                </div>

                {/* Content */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-xl text-amber-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {benefit.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 leading-relaxed ml-9">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Trust Statement */}
        <div
          className={`bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-12 text-center transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-600 rounded-full">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl lg:text-3xl font-serif text-white">
                Our Commitment to Excellence
              </h3>
            </div>
            
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              From intimate gatherings to grand celebrations, we bring the same level of care, 
              precision, and professionalism to every event. Your success is our priority.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-xl text-amber-600"></i>
                <span className="text-sm">Servicing Melbourne &amp; Surrounding Areas</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-xl text-amber-600"></i>
                <span className="text-sm">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-truck-line text-xl text-amber-600"></i>
                <span className="text-sm">Reliable Delivery &amp; Collection</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;